/**
 * 打刻のときに業務（作業）を選ぶカード。業務開始・業務終了で共通。
 *
 * 以前は船の業務種別と特別作業を全部（多い船で60枚以上）大きなカードで並べていたため、
 * 目的のカードまで何度もスクロールが必要で、横向きのタブレットでは下の段が切れて
 * 白紙のカードが並んでいるように見えた。
 *  - よく使う（この端末・この船で最近選んだ）ものを先頭に、最初は数枚だけ出す
 *  - 残りは「ほかの作業を表示」で開く。労働時間の扱いが違うもの（食事・非常作業など）は見出しで分ける
 *  - カードは低く横長にし、タブレットでは3列にする
 */
import React, { useMemo, useState } from 'react';

export type WorkBucket = 'work' | 'exempt' | 'nonwork';

export interface PickItem {
  /** 選択の識別子（呼び出し側で w:業務種別ID / s:特別作業ID などにする） */
  key: string;
  label: string;
  emoji?: string;
  /** アルコール検査が必要な業務 */
  alco?: boolean;
  /** 労働時間の扱い（見出しの振り分けに使う） */
  bucket?: WorkBucket;
}

const BUCKET_TITLE: Record<WorkBucket, { title: string; color: string; note: string }> = {
  work:    { title: 'ほかの作業',       color: '#334155', note: '' },
  exempt:  { title: '緊急・安全対応',   color: '#dc2626', note: '労働時間の上限とは別枠で記録します' },
  nonwork: { title: '労働時間外の記録', color: '#64748b', note: '労働時間には含めません' },
};

// ── よく使う順（端末ごと・船ごとに覚える。共有端末なら「この船でよく使う」になる） ──
type Usage = Record<string, { n: number; t: number }>;
const usageKey = (scope: string) => `catms_wt_usage:${scope}`;

function loadUsage(scope: string): Usage {
  try { return JSON.parse(localStorage.getItem(usageKey(scope)) ?? '{}') as Usage; } catch { return {}; }
}

/** 選んで進んだ業務を記録する（業務開始・業務終了の「進む」ボタンで呼ぶ） */
export function recordWorkUsage(scope: string | null | undefined, keys: string[]): void {
  if (!scope || keys.length === 0) return;
  try {
    const u = loadUsage(scope);
    const now = Date.now();
    for (const k of keys) u[k] = { n: (u[k]?.n ?? 0) + 1, t: now };
    localStorage.setItem(usageKey(scope), JSON.stringify(u));
  } catch { /* 覚えられなくても選ぶことはできる */ }
}

interface Props {
  items: PickItem[];
  selected: string[];
  onToggle: (key: string) => void;
  /** よく使う順を覚える単位（船ID） */
  usageScope?: string | null;
  /** 最初に出す枚数 */
  initialCount?: number;
  /** 選んだときの色 */
  accent?: string;
}

export const WorkTypePicker: React.FC<Props> = ({ items, selected, onToggle, usageScope, initialCount = 8, accent = '#0057d9' }) => {
  const [expanded, setExpanded] = useState(false);

  const { top, rest } = useMemo(() => {
    const u = usageScope ? loadUsage(usageScope) : {};
    const order = new Map(items.map((it, i) => [it.key, i]));
    const used = items.filter(it => (u[it.key]?.n ?? 0) > 0)
      .sort((a, b) => (u[b.key]!.n - u[a.key]!.n) || (u[b.key]!.t - u[a.key]!.t) || (order.get(a.key)! - order.get(b.key)!));
    const unusedWork = items.filter(it => !(u[it.key]?.n) && (it.bucket ?? 'work') === 'work');
    // 枚数が少なければ畳まずに全部出す
    if (items.length <= initialCount + 2) return { top: items, rest: [] as PickItem[] };
    const topKeys = new Set([...used, ...unusedWork].slice(0, initialCount).map(it => it.key));
    // 選んでいるものは畳んだ側に隠さない
    for (const k of selected) topKeys.add(k);
    const topList = items.filter(it => topKeys.has(it.key))
      .sort((a, b) => {
        const ua = u[a.key]?.n ?? 0, ub = u[b.key]?.n ?? 0;
        if (ua !== ub) return ub - ua;
        return order.get(a.key)! - order.get(b.key)!;
      });
    return { top: topList, rest: items.filter(it => !topKeys.has(it.key)) };
  // 選択のたびに並びが動くと押し間違えるので、並びは開いた時点で決める（selected は依存に入れない）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, usageScope, initialCount]);

  const restGroups = useMemo(() => {
    const order: WorkBucket[] = ['work', 'exempt', 'nonwork'];
    return order.map(b => ({ bucket: b, list: rest.filter(it => (it.bucket ?? 'work') === b) })).filter(g => g.list.length > 0);
  }, [rest]);

  const card = (it: PickItem) => {
    const on = selected.includes(it.key);
    return (
      <button key={it.key} type="button" onClick={() => onToggle(it.key)} aria-pressed={on}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, minHeight: 54, padding: '8px 10px', borderRadius: 12,
          border: on ? `2px solid ${accent}` : '1.5px solid #cbd5e1', background: on ? accent : '#fff',
          color: on ? '#fff' : '#1e293b', cursor: 'pointer', textAlign: 'left', minWidth: 0,
          boxShadow: on ? '0 3px 10px rgba(0,87,217,0.28)' : '0 1px 2px rgba(0,0,0,0.05)',
        }}>
        {it.emoji && <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{it.emoji}</span>}
        <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, lineHeight: 1.25, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>{it.label}</span>
        {it.alco && (
          <span title="アルコール検査が必要" style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, borderRadius: 4, padding: '1px 5px', background: on ? 'rgba(255,255,255,0.25)' : '#fffbeb', color: on ? '#fff' : '#b45309', border: on ? '1px solid rgba(255,255,255,0.4)' : '1px solid #fde68a' }}>酒</span>
        )}
        {on && <span style={{ flexShrink: 0, fontSize: 14, fontWeight: 900 }}>✓</span>}
      </button>
    );
  };

  if (items.length === 0) return null;

  return (
    <div style={{ marginBottom: 6 }}>
      {rest.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', margin: '0 2px 6px' }}>よく使う作業</div>
      )}
      <div className="work-pick-grid">{top.map(card)}</div>

      {rest.length > 0 && (
        <>
          <button type="button" onClick={() => setExpanded(v => !v)}
            style={{ width: '100%', marginTop: 10, padding: '11px 12px', borderRadius: 12, border: '1.5px dashed #94a3b8', background: '#f8fafc', color: '#334155', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            {expanded ? 'ほかの作業を閉じる ▲' : `ほかの作業を表示（${rest.length}件） ▼`}
          </button>
          {expanded && restGroups.map(g => (
            <div key={g.bucket} style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '0 2px 6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: BUCKET_TITLE[g.bucket].color }}>{BUCKET_TITLE[g.bucket].title}</span>
                {BUCKET_TITLE[g.bucket].note && <span style={{ fontSize: 11, color: '#94a3b8' }}>{BUCKET_TITLE[g.bucket].note}</span>}
              </div>
              <div className="work-pick-grid">{g.list.map(card)}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default WorkTypePicker;
